import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Button,
  DatePicker,
  Select,
  Radio,
  Table,
  Tooltip,
  Modal,
  Form,
  InputNumber,
  Input,
  message,
  Space,
  Typography,
} from 'antd';
import { PlusOutlined, DownloadOutlined, ReloadOutlined, UnorderedListOutlined, QuestionCircleFilled } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import StatsKPIGroup, { KPIItem } from '../../components/Business/StatsKPIGroup';
import {
  cashflowDataApi,
  type CashflowDataRequest,
  type CashflowDataResponse,
  type CashflowDataType,
  type CashflowDimension,
  type CashflowManualDataRequest,
  type PeriodData,
} from '../../services/statistics/cashflow-data';
import { useShopMap } from '../../services/store/organization.store';
import { OrderType } from '../../types/process/order';
import './CashflowData.css';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TextArea } = Input;

interface TableDataItem {
  key: string;
  name: string;
  isParent?: boolean;
  children?: TableDataItem[];
  [key: string]: unknown;
}

type CashflowReceivedRentType = 'basic_rent' | 'prepaid_rent' | 'overdue_recovery' | 'bad_debt_recovery';

const safeNumber = (value: unknown): number => {
  if (typeof value !== 'number' || Number.isNaN(value) || !Number.isFinite(value)) {
    return 0;
  }
  return value;
};

const formatCurrency = (value: number): string => {
  return `¥${Math.round(safeNumber(value)).toLocaleString('zh-CN')}`;
};

const getDateSpanDays = (start: Dayjs, end: Dayjs): number => {
  return end.startOf('day').diff(start.startOf('day'), 'day') + 1;
};

const getAllowedDimensions = (start: Dayjs, end: Dayjs): CashflowDimension[] => {
  const days = getDateSpanDays(start, end);
  if (days <= 31) {
    if (days >= 28) {
      return ['day', 'week'];
    }
    return ['day'];
  }
  if (days < 365) {
    return ['week', 'month'];
  }
  return ['month'];
};

const getDefaultDateRange = (): [Dayjs, Dayjs] => [
  dayjs().subtract(11, 'month').startOf('month'),
  dayjs().endOf('month'),
];

const DEFAULT_EXPANDED_ROW_KEYS: React.Key[] = ['2', '4'];

const normalizeDateToken = (value: string): string => value.trim().replace(/-/g, '/');

const formatDateObject = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}/${m}/${d}`;
};

const isoWeekStart = (year: number, week: number): Date => {
  const safeWeek = week <= 0 ? 1 : week;
  const jan4 = new Date(year, 0, 4);
  const jan4Weekday = jan4.getDay() === 0 ? 7 : jan4.getDay();
  const firstMonday = new Date(year, 0, 4 - (jan4Weekday - 1));
  return new Date(firstMonday.getFullYear(), firstMonday.getMonth(), firstMonday.getDate() + (safeWeek - 1) * 7);
};

const parseWeekRangeDatesFromKey = (weekKey: string): { start: Date; end: Date } | undefined => {
  const key = weekKey.trim();
  if (!key) {
    return undefined;
  }

  let year: number | undefined;
  let week: number | undefined;

  if (/^\d{6}$/.test(key)) {
    year = Number(key.slice(0, 4));
    week = Number(key.slice(4));
  } else {
    const parts = key.split('-');
    if (parts.length === 2 && /^\d{4}$/.test(parts[0]) && /^\d{1,2}$/.test(parts[1])) {
      year = Number(parts[0]);
      week = Number(parts[1]) + 1; // SQLite %W 是 00-based，转为 ISO 1-based 周序
    }
  }

  if (!year || !week || week < 1 || week > 53) {
    return undefined;
  }

  const start = isoWeekStart(year, week);
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return { start, end };
};

const parseWeekRangeFromKey = (weekKey: string): string | undefined => {
  const dateRange = parseWeekRangeDatesFromKey(weekKey);
  if (!dateRange) {
    return undefined;
  }
  const { start, end } = dateRange;
  return `${formatDateObject(start)} - ${formatDateObject(end)}`;
};

const getPeriodDateRangeForFilter = (
  period: PeriodData,
  dimension: CashflowDimension,
  selectedStartDate?: string,
  selectedEndDate?: string,
): { startDate: string; endDate: string } | undefined => {
  const periodKey = String(period.period || '').trim();
  if (!periodKey) {
    return undefined;
  }

  const clampToSelectedRange = (range: { startDate: string; endDate: string }) => {
    if (!selectedStartDate || !selectedEndDate) {
      return range;
    }
    const rangeStart = dayjs(range.startDate, 'YYYY-MM-DD', true);
    const rangeEnd = dayjs(range.endDate, 'YYYY-MM-DD', true);
    const selectedStart = dayjs(selectedStartDate, 'YYYY-MM-DD', true);
    const selectedEnd = dayjs(selectedEndDate, 'YYYY-MM-DD', true);

    if (!rangeStart.isValid() || !rangeEnd.isValid() || !selectedStart.isValid() || !selectedEnd.isValid()) {
      return range;
    }

    const nextStart = rangeStart.isBefore(selectedStart) ? selectedStart : rangeStart;
    const nextEnd = rangeEnd.isAfter(selectedEnd) ? selectedEnd : rangeEnd;
    if (nextStart.isAfter(nextEnd)) {
      return undefined;
    }
    return {
      startDate: nextStart.format('YYYY-MM-DD'),
      endDate: nextEnd.format('YYYY-MM-DD'),
    };
  };

  if (dimension === 'day') {
    const parsed = dayjs(periodKey, 'YYYY-MM-DD', true);
    if (!parsed.isValid()) {
      return undefined;
    }
    const date = parsed.format('YYYY-MM-DD');
    return clampToSelectedRange({ startDate: date, endDate: date });
  }

  if (dimension === 'week') {
    const dateRange = parseWeekRangeDatesFromKey(periodKey);
    if (!dateRange) {
      return undefined;
    }
    return clampToSelectedRange({
      startDate: dayjs(dateRange.start).format('YYYY-MM-DD'),
      endDate: dayjs(dateRange.end).format('YYYY-MM-DD'),
    });
  }

  const monthParsed = dayjs(periodKey, 'YYYY-MM', true);
  if (monthParsed.isValid()) {
    return clampToSelectedRange({
      startDate: monthParsed.startOf('month').format('YYYY-MM-DD'),
      endDate: monthParsed.endOf('month').format('YYYY-MM-DD'),
    });
  }
  return undefined;
};

const getPeriodHeaderLabel = (period: PeriodData, dimension: CashflowDimension): string => {
  const rawLabel = String(period.periodLabel || '').trim();
  const rawPeriod = String(period.period || '').trim();

  if (dimension === 'day') {
    if (/^\d{4}[/-]\d{2}[/-]\d{2}$/.test(rawLabel)) {
      return normalizeDateToken(rawLabel);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(rawPeriod)) {
      return normalizeDateToken(rawPeriod);
    }
    return rawLabel || rawPeriod;
  }

  if (dimension === 'week') {
    const labelMatch = rawLabel.match(/(\d{4}[/-]\d{2}[/-]\d{2})\s*[-~]\s*(\d{4}[/-]\d{2}[/-]\d{2})/);
    if (labelMatch) {
      return `${normalizeDateToken(labelMatch[1])} - ${normalizeDateToken(labelMatch[2])}`;
    }
    const rangeFromKey = parseWeekRangeFromKey(rawPeriod);
    if (rangeFromKey) {
      return rangeFromKey;
    }
    return rawLabel || rawPeriod;
  }

  if (dimension === 'month') {
    if (/^\d{4}-\d{2}$/.test(rawPeriod)) {
      const [year, month] = rawPeriod.split('-');
      return `${year}年${Number(month)}月`;
    }
    return rawLabel || rawPeriod;
  }

  return rawLabel || rawPeriod;
};

const MANUAL_PENDING_ROW_KEYS = new Set(['1', '2.2', '2.3', '2.4', '3.2', '3.3', '4.3']);
const EMPHASIZED_PROJECT_ROW_KEYS = new Set(['2.1', '2.2', '2.3', '2.4']);

const calculateKpiFromPeriods = (periods: PeriodData[]) => {
  let totalCapitalInjection = 0;
  let totalIncome = 0;
  let totalExpense = 0;
  let totalBalance = 0;

  periods.forEach((period) => {
    const items = period.items;
    const received = items?.income?.receivedRent;
    const managementFee = items?.expense?.managementFee;

    const basicRent = safeNumber(received?.basicRent);
    const prepaidRent = safeNumber(received?.prepaidRent);
    const overdueRecovery = safeNumber(received?.overdueRecovery);
    const badDebtRecovery = safeNumber(received?.badDebtRecovery);
    const penaltyIncome = safeNumber(items?.income?.penaltyIncome);
    const buyoutIncome = safeNumber(items?.income?.buyoutIncome);
    const disposalIncome = safeNumber(items?.income?.disposalIncome);

    const commissionExpense = safeNumber(items?.expense?.commissionExpense);
    const purchaseCost = safeNumber(items?.expense?.purchaseCost);
    const electricityFee = safeNumber(managementFee?.electricityFee);
    const rentFee = safeNumber(managementFee?.rentFee);
    const propertyFee = safeNumber(managementFee?.propertyFee);
    const laborCost = safeNumber(managementFee?.laborCost);
    const badDebt = safeNumber(items?.expense?.badDebt);

    const totalReceivedRent = basicRent + prepaidRent + overdueRecovery + badDebtRecovery;
    const periodIncome = totalReceivedRent + penaltyIncome + buyoutIncome + disposalIncome;
    const periodManagementFee = electricityFee + rentFee + propertyFee + laborCost;
    const periodExpense = commissionExpense + purchaseCost + periodManagementFee + badDebt;

    totalCapitalInjection += safeNumber(items?.capitalInjection);
    totalIncome += periodIncome;
    totalExpense += periodExpense;
    totalBalance += safeNumber(items?.balance);
  });

  return {
    totalCapitalInjection,
    totalIncome,
    totalExpense,
    totalBalance,
  };
};

const calculateProjectColumnWidth = (rows: TableDataItem[], expandedKeys: React.Key[]): number => {
  if (rows.length === 0) {
    return 128;
  }

  const expandedKeySet = new Set(expandedKeys.map((key) => String(key)));
  let maxVisibleDepth = 1;

  const walk = (list: TableDataItem[], depth: number) => {
    list.forEach((row) => {
      if (depth > maxVisibleDepth) {
        maxVisibleDepth = depth;
      }
      if (!row.children || row.children.length === 0) {
        return;
      }
      if (!expandedKeySet.has(String(row.key))) {
        return;
      }
      walk(row.children, depth + 1);
    });
  };

  walk(rows, 1);
  return 128 + (maxVisibleDepth - 1) * 28;
};

const buildTableData = (periods: PeriodData[], includeManagementFee: boolean): TableDataItem[] => {
  const valueMap: Record<string, Record<string, number>> = {};
  const periodKeys = periods.map((period) => period.period);
  const receivableHintMap: Record<string, number> = {};
  const managementElectricityMap: Record<string, number> = {};
  const managementRentMap: Record<string, number> = {};
  const managementPropertyMap: Record<string, number> = {};
  const managementLaborMap: Record<string, number> = {};

  const setValue = (rowKey: string, periodKey: string, value: number) => {
    if (!valueMap[rowKey]) {
      valueMap[rowKey] = {};
    }
    valueMap[rowKey][periodKey] = value;
  };

  periods.forEach((period) => {
    const periodKey = period.period;
    const items = period.items;
    const received = items?.income?.receivedRent;
    const managementFee = items?.expense?.managementFee;

    const basicRent = safeNumber(received?.basicRent);
    const prepaidRent = safeNumber(received?.prepaidRent);
    const overdueRecovery = safeNumber(received?.overdueRecovery);
    const badDebtRecovery = safeNumber(received?.badDebtRecovery);
    const penaltyIncome = safeNumber(items?.income?.penaltyIncome);
    const buyoutIncome = safeNumber(items?.income?.buyoutIncome);
    const disposalIncome = safeNumber(items?.income?.disposalIncome);

    const devicePurchaseFund = safeNumber(items?.receivable?.devicePurchaseFund);
    const receivableIncome = safeNumber(items?.receivable?.receivableIncome);
    const inventoryResidualValue = safeNumber(items?.receivable?.inventoryResidualValue);

    const commissionExpense = safeNumber(items?.expense?.commissionExpense);
    const purchaseCost = safeNumber(items?.expense?.purchaseCost);
    const electricityFee = safeNumber(managementFee?.electricityFee);
    const rentFee = safeNumber(managementFee?.rentFee);
    const propertyFee = safeNumber(managementFee?.propertyFee);
    const laborCost = safeNumber(managementFee?.laborCost);
    const badDebt = safeNumber(items?.expense?.badDebt);

    const totalReceivedRent = basicRent + prepaidRent + overdueRecovery + badDebtRecovery;
    const totalIncome = totalReceivedRent + penaltyIncome + buyoutIncome + disposalIncome;
    const totalReceivable = receivableIncome;
    const totalManagementFee = electricityFee + rentFee + propertyFee + laborCost;
    const totalExpense = commissionExpense + purchaseCost + totalManagementFee + badDebt;
    const totalInventoryTurnover = devicePurchaseFund + disposalIncome + inventoryResidualValue;

    setValue('1', periodKey, safeNumber(items?.capitalInjection));

    setValue('2', periodKey, totalIncome);
    setValue('2.1', periodKey, totalReceivedRent);
    setValue('2.1.1', periodKey, basicRent);
    setValue('2.1.2', periodKey, prepaidRent);
    setValue('2.1.3', periodKey, overdueRecovery);
    setValue('2.1.4', periodKey, badDebtRecovery);
    setValue('2.2', periodKey, penaltyIncome);
    setValue('2.3', periodKey, buyoutIncome);
    setValue('2.4', periodKey, disposalIncome);
    receivableHintMap[periodKey] = totalReceivable;

    setValue('4', periodKey, totalExpense);
    setValue('4.1', periodKey, commissionExpense);
    setValue('4.2', periodKey, purchaseCost);
    if (includeManagementFee) {
      setValue('4.3', periodKey, totalManagementFee);
      managementElectricityMap[periodKey] = electricityFee;
      managementRentMap[periodKey] = rentFee;
      managementPropertyMap[periodKey] = propertyFee;
      managementLaborMap[periodKey] = laborCost;
    }
    setValue('4.4', periodKey, badDebt);

    setValue('3', periodKey, totalInventoryTurnover);
    setValue('3.1', periodKey, devicePurchaseFund);
    setValue('3.2', periodKey, disposalIncome);
    setValue('3.3', periodKey, inventoryResidualValue);

    setValue('5', periodKey, safeNumber(items?.balance));
  });

  const withPeriods = (row: TableDataItem): TableDataItem => {
    const nextRow: TableDataItem = { ...row };
    periodKeys.forEach((periodKey) => {
      nextRow[periodKey] = valueMap[row.key]?.[periodKey] ?? 0;
      if (row.key === '2.1') {
        nextRow[`${periodKey}__receivable`] = receivableHintMap[periodKey] ?? 0;
      }
      if (row.key === '4.3') {
        nextRow[`${periodKey}__management_electricity`] = managementElectricityMap[periodKey] ?? 0;
        nextRow[`${periodKey}__management_rent`] = managementRentMap[periodKey] ?? 0;
        nextRow[`${periodKey}__management_property`] = managementPropertyMap[periodKey] ?? 0;
        nextRow[`${periodKey}__management_labor`] = managementLaborMap[periodKey] ?? 0;
      }
    });
    if (row.children) {
      nextRow.children = row.children.map(withPeriods);
    }
    return nextRow;
  };

  const rows: TableDataItem[] = [
    { key: '1', name: '1. 资金注入', isParent: true },
    {
      key: '2',
      name: '2. 收入',
      isParent: true,
      children: [
        {
          key: '2.1',
          name: '2.1 已收租金',
          isParent: true,
          children: [
            { key: '2.1.1', name: '2.1.1 基础租金' },
            { key: '2.1.2', name: '2.1.2 预收租金' },
            { key: '2.1.3', name: '2.1.3 逾期回收款' },
            { key: '2.1.4', name: '2.1.4 坏账追回款' },
          ],
        },
        { key: '2.2', name: '2.2 违约金收入' },
        { key: '2.3', name: '2.3 买断收入' },
        { key: '2.4', name: '2.4 处置收入' },
      ],
    },
    {
      key: '3',
      name: '3. 库存流转',
      isParent: true,
      children: [
        { key: '3.1', name: '3.1 设备采购金' },
        { key: '3.2', name: '3.2 处置收入' },
        { key: '3.3', name: '3.3 库存残值' },
      ],
    },
    {
      key: '4',
      name: '4. 支出',
      isParent: true,
      children: includeManagementFee
        ? [
          { key: '4.1', name: '4.1 佣金支出' },
          { key: '4.2', name: '4.2 采购总成本' },
          { key: '4.3', name: '4.3 管理费用' },
          { key: '4.4', name: '4.4 坏账' },
        ]
        : [
          { key: '4.1', name: '4.1 佣金支出' },
          { key: '4.2', name: '4.2 采购总成本' },
          { key: '4.4', name: '4.4 坏账' },
        ],
    },
    { key: '5', name: '5. 结余', isParent: true },
  ];

  return rows.map(withPeriods);
};

const CashflowData: React.FC = () => {
  const navigate = useNavigate();
  const { shopList } = useShopMap();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(getDefaultDateRange);
  const [storeId, setStoreId] = useState<number | undefined>(undefined);
  const [timeDimension, setTimeDimension] = useState<CashflowDimension>('month');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([...DEFAULT_EXPANDED_ROW_KEYS]);

  const [kpiData, setKpiData] = useState({
    totalCapitalInjection: 0,
    totalIncome: 0,
    totalExpense: 0,
    totalBalance: 0,
  });
  const [periods, setPeriods] = useState<PeriodData[]>([]);
  const [tableData, setTableData] = useState<TableDataItem[]>([]);
  const modalStoreId = Form.useWatch('storeId', form);
  const isAllStoreView = storeId === undefined;
  const dimensionLabel = useMemo(() => {
    if (timeDimension === 'day') {
      return '日';
    }
    if (timeDimension === 'week') {
      return '周';
    }
    return '月';
  }, [timeDimension]);
  const timeAggregationHint = useMemo(() => `按${dimensionLabel}维度聚合展示`, [dimensionLabel]);

  const projectRemarkMap = useMemo<Record<string, string>>(() => ({
    '1': `资金注入：人工录入，来源 cashflow_entry_logs.capital_injection，按 record_date 落入统计区间，${timeAggregationHint}。`,
    '2': '收入汇总：2.1 + 2.2 + 2.3 + 2.4。',
    '2.1': `已收租金汇总：2.1.1 + 2.1.2 + 2.1.3 + 2.1.4；统计区间以计算时间（优先结算时间，缺失时回退实付日期）判断，金额统一取结算金额，${timeAggregationHint}。`,
    '2.1.1': '基础租金：支付状态=支付完成，且计算时间（优先结算时间）<= 应扣日期；统计区间基于计算时间，金额取结算金额。',
    '2.1.2': '预收租金：支付状态=支付完成，且命中“首期支付后 10 天窗口内的后续期数”规则；统计区间基于计算时间（优先结算时间），金额取结算金额。',
    '2.1.3': '逾期回收款：支付状态=支付完成，且计算时间晚于应扣日期并且逾期 <= 30 天；统计区间基于计算时间（优先结算时间），金额取结算金额。',
    '2.1.4': '坏账追回款：支付状态=支付完成，且计算时间晚于应扣日期并且逾期 > 30 天；统计区间基于计算时间（优先结算时间），金额取结算金额。',
    '2.2': `违约金收入：人工录入，来源 cashflow_entry_logs.penalty_income，按 record_date 落入统计区间，${timeAggregationHint}。`,
    '2.3': `买断收入：人工录入，来源 cashflow_entry_logs.buyout_income，按 record_date 落入统计区间，${timeAggregationHint}。`,
    '2.4': `处置收入：人工录入，来源 cashflow_entry_logs.disposal_income，按 record_date 落入统计区间，${timeAggregationHint}。`,
    '3': '库存流转汇总：3.1 + 3.2 + 3.3。',
    '3.1': '设备采购金：按“采购明细生成时间”（即设备费支付时间）归属统计区间，跳转明细同样按该时间筛选。',
    '3.2': `处置收入：人工录入（与 2.4 同口径），按 record_date 落入统计区间，${timeAggregationHint}。`,
    '3.3': `库存残值：人工录入，仅读取 cashflow_entry_logs.inventory_residual_value，按 record_date 落入统计区间，${timeAggregationHint}；该值为存量数据。`,
    '4': '支出汇总：4.1 + 4.2 + 4.3 + 4.4。',
    '4.1': '佣金支出：与经营数据总览口径一致，基于人人租财务明细（deposits）中“线下支付倒扣商家/平台分账/退回分账”三类记录计算；按 main.created_at（佣金生成时间）归属统计区间，点击金额可查看人人租财务明细。',
    '4.2': '采购总成本：按“采购明细生成时间”（即设备费支付时间）归属统计区间，跳转明细同样按该时间筛选。',
    '4.3': `管理费用：来自现金流人工录入（水电/房租/物业/人力），按 record_date 落入统计区间，${timeAggregationHint}。`,
    '4.4': '坏账：未支付且逾期超过 30 天的账单金额汇总；统计区间基于应扣日期 expectedDeductionDate，点击金额可查看账单明细。',
    '5': '结余：资金注入 + 收入 - 支出。',
  }), [timeAggregationHint]);

  const jumpToInstallmentLedgerBySettlementTimeForRentType = useCallback((period: PeriodData, rentType?: CashflowReceivedRentType) => {
    const periodDateRange = getPeriodDateRangeForFilter(
      period,
      timeDimension,
      dateRange[0].format('YYYY-MM-DD'),
      dateRange[1].format('YYYY-MM-DD'),
    );
    if (!periodDateRange) {
      message.warning('未能识别当前周期的日期范围');
      return;
    }

    const filter: Record<string, any> = {
      settlementTime: {
        operator: 'between',
        value: [periodDateRange.startDate, periodDateRange.endDate],
      },
      order_type: {
        operator: 'eq',
        value: OrderType.Normal,
      },
      aftersale_status: {
        operator: 'ne',
        value: '已退款',
      },
      paymentStatus: {
        operator: 'eq',
        value: '支付完成',
      },
    };

    if (storeId !== undefined) {
      filter.storeId = {
        operator: 'eq',
        value: storeId,
      };
    }
    if (rentType) {
      filter.cashflow_received_rent_type = {
        operator: 'eq',
        value: rentType,
      };
    }

    window.open(`/financial/installment-ledger?q=${encodeURIComponent(JSON.stringify({ filter }))}`, '_blank');
  }, [storeId, timeDimension, dateRange]);

  const jumpToInstallmentLedgerBySettlementTime = useCallback((period: PeriodData) => {
    const periodDateRange = getPeriodDateRangeForFilter(
      period,
      timeDimension,
      dateRange[0].format('YYYY-MM-DD'),
      dateRange[1].format('YYYY-MM-DD'),
    );
    if (!periodDateRange) {
      message.warning('未能识别当前周期的日期范围');
      return;
    }

    const filter: Record<string, any> = {
      settlementTime: {
        operator: 'between',
        value: [periodDateRange.startDate, periodDateRange.endDate],
      },
      order_type: {
        operator: 'eq',
        value: OrderType.Normal,
      },
      aftersale_status: {
        operator: 'ne',
        value: '已退款',
      },
    };

    if (storeId !== undefined) {
      filter.storeId = {
        operator: 'eq',
        value: storeId,
      };
    }

    window.open(`/financial/installment-ledger?q=${encodeURIComponent(JSON.stringify({ filter }))}`, '_blank');
  }, [storeId, timeDimension, dateRange]);

  const jumpToInstallmentLedgerByExpectedDeductionDateForBadDebt = useCallback((period: PeriodData) => {
    const periodDateRange = getPeriodDateRangeForFilter(
      period,
      timeDimension,
      dateRange[0].format('YYYY-MM-DD'),
      dateRange[1].format('YYYY-MM-DD'),
    );
    if (!periodDateRange) {
      message.warning('未能识别当前周期的日期范围');
      return;
    }

    const filter: Record<string, any> = {
      expectedDeductionDate: {
        operator: 'between',
        value: [periodDateRange.startDate, periodDateRange.endDate],
      },
      order_type: {
        operator: 'eq',
        value: OrderType.Normal,
      },
      isBadDebt: {
        operator: 'eq',
        value: true,
      },
      paymentStatus: {
        operator: 'eq',
        value: '未支付',
      },
    };

    if (storeId !== undefined) {
      filter.storeId = {
        operator: 'eq',
        value: storeId,
      };
    }

    window.open(`/financial/installment-ledger?q=${encodeURIComponent(JSON.stringify({ filter }))}`, '_blank');
  }, [storeId, timeDimension, dateRange]);

  const jumpToOrderListByPurchaseDetailGeneratedAt = useCallback((period: PeriodData) => {
    const periodDateRange = getPeriodDateRangeForFilter(
      period,
      timeDimension,
      dateRange[0].format('YYYY-MM-DD'),
      dateRange[1].format('YYYY-MM-DD'),
    );
    if (!periodDateRange) {
      message.warning('未能识别当前周期的日期范围');
      return;
    }

    const filter: Record<string, any> = {
      purchase_detail_generated_at: {
        operator: 'between',
        value: [periodDateRange.startDate, periodDateRange.endDate],
      },
      order_type: {
        operator: 'eq',
        value: OrderType.Normal,
      },
      aftersale_status: {
        operator: 'ne',
        value: '已退款',
      },
      rrzu_status: {
        operator: 'in',
        value: [3, 4, 5, 19, 20],
      },
    };

    if (storeId !== undefined) {
      filter.store_id = {
        operator: 'in',
        value: [storeId],
      };
    }

    window.open(`/order/list?q=${encodeURIComponent(JSON.stringify({ filter }))}`, '_blank');
  }, [storeId, timeDimension, dateRange]);

  const jumpToRRZFinancialLedgerByCommissionDate = useCallback((period: PeriodData) => {
    const periodDateRange = getPeriodDateRangeForFilter(
      period,
      timeDimension,
      dateRange[0].format('YYYY-MM-DD'),
      dateRange[1].format('YYYY-MM-DD'),
    );
    if (!periodDateRange) {
      message.warning('未能识别当前周期的日期范围');
      return;
    }

    const filter: Record<string, any> = {
      cashflowCommissionGeneratedAt: {
        operator: 'between',
        value: [periodDateRange.startDate, periodDateRange.endDate],
      },
      order_type: {
        operator: 'eq',
        value: OrderType.Normal,
      },
      body: {
        operator: 'regex',
        value: '线下支付倒扣商家|平台分账|退回分账',
      },
    };
    if (storeId !== undefined) {
      filter.storeId = {
        operator: 'eq',
        value: storeId,
      };
    }

    window.open(`/financial/rrz-financial-ledger?q=${encodeURIComponent(JSON.stringify({ filter }))}`, '_blank');
  }, [storeId, timeDimension, dateRange]);

  useEffect(() => {
    if (modalStoreId !== undefined) {
      form.setFieldsValue({
        electricityFee: undefined,
        rentFee: undefined,
        propertyFee: undefined,
        laborCost: undefined,
      });
    }
  }, [modalStoreId, form]);

  const availableDimensions = useMemo(() => {
    return getAllowedDimensions(dateRange[0], dateRange[1]);
  }, [dateRange]);

  useEffect(() => {
    if (!availableDimensions.includes(timeDimension)) {
      setTimeDimension(availableDimensions[0]);
    }
  }, [availableDimensions, timeDimension]);

  const handleQuery = useCallback(() => {
    if (!availableDimensions.includes(timeDimension)) {
      message.warning('当前日期范围不支持所选时间维度');
      return;
    }
    setRefreshTrigger((prev) => prev + 1);
  }, [availableDimensions, timeDimension]);

  const handleTimeDimensionChange = useCallback((nextDimension: CashflowDimension) => {
    setTimeDimension(nextDimension);
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const fetchData = async () => {
    if (!availableDimensions.includes(timeDimension)) {
      return;
    }

    setLoading(true);
    try {
      const params: CashflowDataRequest = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        storeId,
        dimension: timeDimension,
      };

      const response = await cashflowDataApi.getStatistics(params);
      const data: CashflowDataResponse = response.data;
      const responsePeriods = data.periods || [];
      const kpiSummary = calculateKpiFromPeriods(responsePeriods);

      setKpiData({
        totalCapitalInjection: kpiSummary.totalCapitalInjection,
        totalIncome: kpiSummary.totalIncome,
        totalExpense: kpiSummary.totalExpense,
        totalBalance: kpiSummary.totalBalance,
      });
      setPeriods(responsePeriods);
      setTableData(buildTableData(responsePeriods, isAllStoreView));
      setExpandedRowKeys([...DEFAULT_EXPANDED_ROW_KEYS]);
    } catch (error) {
      console.error('获取数据失败:', error);
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [refreshTrigger]);

  const projectColumnWidth = useMemo(
    () => calculateProjectColumnWidth(tableData, expandedRowKeys),
    [tableData, expandedRowKeys],
  );

  const columns: ColumnsType<TableDataItem> = useMemo(() => {
    const periodColumnWidth = timeDimension === 'month'
      ? (periods.length >= 12 ? 60 : 80)
      : (timeDimension === 'week' ? 172 : 92);
    const dynamicColumns: ColumnsType<TableDataItem> = [
      {
        title: '项目',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: projectColumnWidth,
        align: 'center',
        onHeaderCell: () => ({ className: 'cashflow-project-header' }),
        onCell: () => ({ className: 'cashflow-project-cell' }),
        render: (text: string, record) => (
          <span className="cashflow-project-with-tip">
            <Text strong={record.isParent || EMPHASIZED_PROJECT_ROW_KEYS.has(String(record.key))} className="cashflow-project-label">{text}</Text>
            {projectRemarkMap[record.key] ? (
              <Tooltip title={projectRemarkMap[record.key]}>
                <QuestionCircleFilled className="cashflow-project-tip-icon" />
              </Tooltip>
            ) : null}
          </span>
        ),
      },
    ];

    periods.forEach((period) => {
      const headerLabel = getPeriodHeaderLabel(period, timeDimension);
      dynamicColumns.push({
        title: (
          <div className="cashflow-period-header">
            <div className="cashflow-period-label">{headerLabel}</div>
          </div>
        ),
        dataIndex: period.period,
        key: period.period,
        width: periodColumnWidth,
        align: 'center',
        render: (value: unknown, record) => {
          const currentValue = safeNumber(value);
          const valueClassName = record.key === '4'
            ? 'cashflow-cell-value cashflow-value-expense'
            : (record.key === '5' ? 'cashflow-cell-value cashflow-value-balance' : 'cashflow-cell-value');

          if (MANUAL_PENDING_ROW_KEYS.has(String(record.key))) {
            if (record.key === '4.3') {
              const electricity = safeNumber(record[`${period.period}__management_electricity`]);
              const rent = safeNumber(record[`${period.period}__management_rent`]);
              const property = safeNumber(record[`${period.period}__management_property`]);
              const labor = safeNumber(record[`${period.period}__management_labor`]);
              const hasManagementInput =
                currentValue !== 0 || electricity !== 0 || rent !== 0 || property !== 0 || labor !== 0;

              if (!hasManagementInput) {
                return (
                  <span className="cashflow-pending-cell">
                    <span className="cashflow-pending-text">待录入</span>
                  </span>
                );
              }
            } else if (currentValue === 0) {
              return (
                <span className="cashflow-pending-cell">
                  <span className="cashflow-pending-text">待录入</span>
                </span>
              );
            }
          }

          const mainValue = formatCurrency(safeNumber(value));
          const rentTypeByKey: Record<string, CashflowReceivedRentType> = {
            '2.1.1': 'basic_rent',
            '2.1.2': 'prepaid_rent',
            '2.1.3': 'overdue_recovery',
            '2.1.4': 'bad_debt_recovery',
          };
          const rentType = rentTypeByKey[record.key];
          if (rentType) {
            return (
              <span
                className={`${valueClassName} cashflow-jump-link`}
                onClick={(event) => {
                  event.stopPropagation();
                  jumpToInstallmentLedgerBySettlementTimeForRentType(period, rentType);
                }}
              >
                {mainValue}
              </span>
            );
          }

          if (record.key === '3.1' || record.key === '4.2') {
            return (
              <span
                className={`${valueClassName} cashflow-jump-link`}
                onClick={(event) => {
                  event.stopPropagation();
                  jumpToOrderListByPurchaseDetailGeneratedAt(period);
                }}
              >
                {mainValue}
              </span>
            );
          }

          if (record.key === '4.1') {
            return (
              <span
                className={`${valueClassName} cashflow-jump-link`}
                onClick={(event) => {
                  event.stopPropagation();
                  jumpToRRZFinancialLedgerByCommissionDate(period);
                }}
              >
                {mainValue}
              </span>
            );
          }

          if (record.key === '4.4') {
            return (
              <span
                className={`${valueClassName} cashflow-jump-link`}
                onClick={(event) => {
                  event.stopPropagation();
                  jumpToInstallmentLedgerByExpectedDeductionDateForBadDebt(period);
                }}
              >
                {mainValue}
              </span>
            );
          }

          if (record.key === '2.1') {
            const receivableValue = safeNumber(record[`${period.period}__receivable`]);
            return (
              <div className="cashflow-cell-with-hint">
                <span className={valueClassName}>{mainValue}</span>
                <span
                  className="cashflow-cell-hint-tag cashflow-jump-link"
                  onClick={(event) => {
                    event.stopPropagation();
                    jumpToInstallmentLedgerBySettlementTime(period);
                  }}
                >
                  应收:{formatCurrency(receivableValue)}
                </span>
              </div>
            );
          }

          if (record.key === '4.3') {
            const electricity = safeNumber(record[`${period.period}__management_electricity`]);
            const rent = safeNumber(record[`${period.period}__management_rent`]);
            const property = safeNumber(record[`${period.period}__management_property`]);
            const labor = safeNumber(record[`${period.period}__management_labor`]);

            return (
              <div className="cashflow-cell-with-detail">
                <span className={valueClassName}>{mainValue}</span>
                <Tooltip
                  placement="top"
                  title={
                    <div>
                      <div>水电费：{formatCurrency(electricity)}</div>
                      <div>物业费：{formatCurrency(property)}</div>
                      <div>房租费：{formatCurrency(rent)}</div>
                      <div>人力成本：{formatCurrency(labor)}</div>
                    </div>
                  }
                >
                  <QuestionCircleFilled className="cashflow-detail-icon" />
                </Tooltip>
              </div>
            );
          }

          return <span className={valueClassName}>{mainValue}</span>;
        },
      });
    });

    return dynamicColumns;
  }, [periods, timeDimension, projectColumnWidth, projectRemarkMap, jumpToInstallmentLedgerBySettlementTimeForRentType, jumpToInstallmentLedgerBySettlementTime, jumpToInstallmentLedgerByExpectedDeductionDateForBadDebt, jumpToOrderListByPurchaseDetailGeneratedAt, jumpToRRZFinancialLedgerByCommissionDate]);

  const kpiItems: KPIItem[] = [
    {
      title: '累计资金注入',
      value: kpiData.totalCapitalInjection,
      prefix: '¥',
      precision: 0,
      color: '#1890ff',
    },
    {
      title: '累计收入',
      value: kpiData.totalIncome,
      prefix: '¥',
      precision: 0,
      color: '#52c41a',
    },
    {
      title: '累计支出',
      value: kpiData.totalExpense,
      prefix: '¥',
      precision: 0,
      color: '#faad14',
    },
    {
      title: '累计结余',
      value: kpiData.totalBalance,
      prefix: '¥',
      precision: 0,
      color: '#f5222d',
    },
  ];

  const handleAddManualData = async () => {
    try {
      const values = await form.validateFields();
      const hasManagementFeeInput =
        typeof values.electricityFee === 'number' ||
        typeof values.rentFee === 'number' ||
        typeof values.propertyFee === 'number' ||
        typeof values.laborCost === 'number';

      if (typeof values.capitalInjection === 'number' && values.storeId === undefined) {
        message.error('资金注入必须选择店铺');
        return;
      }
      if (hasManagementFeeInput && values.storeId !== undefined) {
        message.error('管理费用请在“全部店铺”口径下录入');
        return;
      }

      const payload: CashflowManualDataRequest = {
        recordDate: values.recordDate.format('YYYY-MM-DD'),
        storeId: values.storeId,
        dataType: values.dataType as CashflowDataType,
        remark: values.remark,
      };

      if (typeof values.capitalInjection === 'number' && Number.isFinite(values.capitalInjection)) payload.capitalInjection = values.capitalInjection;
      if (typeof values.penaltyIncome === 'number' && Number.isFinite(values.penaltyIncome)) payload.penaltyIncome = values.penaltyIncome;
      if (typeof values.buyoutIncome === 'number' && Number.isFinite(values.buyoutIncome)) payload.buyoutIncome = values.buyoutIncome;
      if (typeof values.disposalIncome === 'number' && Number.isFinite(values.disposalIncome)) payload.disposalIncome = values.disposalIncome;
      if (typeof values.inventoryResidualValue === 'number' && Number.isFinite(values.inventoryResidualValue)) payload.inventoryResidualValue = values.inventoryResidualValue;
      if (typeof values.electricityFee === 'number' && Number.isFinite(values.electricityFee)) payload.electricityFee = values.electricityFee;
      if (typeof values.rentFee === 'number' && Number.isFinite(values.rentFee)) payload.rentFee = values.rentFee;
      if (typeof values.propertyFee === 'number' && Number.isFinite(values.propertyFee)) payload.propertyFee = values.propertyFee;
      if (typeof values.laborCost === 'number' && Number.isFinite(values.laborCost)) payload.laborCost = values.laborCost;

      await cashflowDataApi.addManualData(payload);
      message.success('添加成功');
      setIsModalVisible(false);
      form.resetFields();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('添加失败:', error);
      message.error('添加失败');
    }
  };

  const handleReset = () => {
    const [defaultStartDate, defaultEndDate] = getDefaultDateRange();
    setDateRange([defaultStartDate, defaultEndDate]);
    setStoreId(undefined);
    setTimeDimension('month');
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleExport = () => {
    message.info('导出功能开发中');
  };

  return (
    <div className="cashflow-data-page">
      <StatsKPIGroup items={kpiItems} />

      <Card style={{ marginTop: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <span>选择日期：</span>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates?.[0] && dates?.[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              format="YYYY-MM-DD"
            />

            <span>主体/店铺：</span>
            <Select
              style={{ width: 220 }}
              placeholder="全部"
              allowClear
              value={storeId}
              onChange={setStoreId}
              options={shopList.map((shop) => ({
                label: shop.name,
                value: Number(shop.code),
              }))}
            />

            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
              新增
            </Button>
            <Button type="primary" onClick={handleQuery}>
              查询
            </Button>
            <Button icon={<UnorderedListOutlined />} onClick={() => navigate('/financial/cashflow-entry-records')}>
              录入记录
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Space>
      </Card>

      <Card
        className="cashflow-overview-card"
        title="现金流数据总览"
        style={{ marginTop: 16 }}
        extra={(
          <Space className="cashflow-table-actions" size="middle">
            <Space size="small">
              <span className="cashflow-table-actions-label">时间维度：</span>
              <Radio.Group value={timeDimension} onChange={(e) => handleTimeDimensionChange(e.target.value as CashflowDimension)}>
                <Radio.Button value="day" disabled={!availableDimensions.includes('day')}>
                  日
                </Radio.Button>
                <Radio.Button value="week" disabled={!availableDimensions.includes('week')}>
                  周
                </Radio.Button>
                <Radio.Button value="month" disabled={!availableDimensions.includes('month')}>
                  月
                </Radio.Button>
              </Radio.Group>
            </Space>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              导出Excel
            </Button>
          </Space>
        )}
      >
        {!isAllStoreView ? (
          <Text type="secondary">当前为单店铺视图，管理费用项仅在“全部店铺”视图展示。</Text>
        ) : null}
        <Table
          className="cashflow-data-table"
          columns={columns}
          dataSource={tableData}
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
          }}
          rowClassName={(record) => (record.isParent ? 'parent-row' : '')}
          bordered
        />
      </Card>

      <Modal
        title="新增数据"
        open={isModalVisible}
        onOk={handleAddManualData}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={700}
        okText="提交数据"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" initialValues={{ dataType: 'daily' }}>
          <Text type="secondary">资金注入必须绑定店铺；管理费用仅支持“全部店铺”口径录入。</Text>
          <Title level={5}>基础信息</Title>
          <Form.Item name="recordDate" label="选择日期" rules={[{ required: true, message: '请选择日期' }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item name="storeId" label="店铺">
            <Select
              placeholder="选择店铺（可选）"
              allowClear
              options={shopList.map((shop) => ({
                label: shop.name,
                value: Number(shop.code),
              }))}
            />
          </Form.Item>

          <Form.Item name="dataType" label="数据类型" rules={[{ required: true, message: '请选择数据类型' }]}>
            <Select
              options={[
                { label: '日数据', value: 'daily' },
                { label: '月数据', value: 'monthly' },
              ]}
            />
          </Form.Item>

          <Title level={5}>资金注入</Title>
          <Form.Item name="capitalInjection" label="资金注入金额">
            <InputNumber style={{ width: '100%' }} prefix="¥" precision={2} min={0} placeholder="请输入金额（元）" />
          </Form.Item>

          <Title level={5}>收入</Title>
          <Form.Item name="penaltyIncome" label="违约金收入">
            <InputNumber style={{ width: '100%' }} prefix="¥" precision={2} min={0} placeholder="请输入金额（元）" />
          </Form.Item>
          <Form.Item name="buyoutIncome" label="买断收入">
            <InputNumber style={{ width: '100%' }} prefix="¥" precision={2} min={0} placeholder="请输入金额（元）" />
          </Form.Item>
          <Form.Item name="disposalIncome" label="处置收入">
            <InputNumber style={{ width: '100%' }} prefix="¥" precision={2} min={0} placeholder="请输入金额（元）" />
          </Form.Item>

          <Title level={5}>库存</Title>
          <Form.Item name="inventoryResidualValue" label="库存残值">
            <InputNumber style={{ width: '100%' }} prefix="¥" precision={2} min={0} placeholder="请输入金额（元）" />
          </Form.Item>

          <Title level={5}>管理费用</Title>
          <Form.Item name="electricityFee" label="水电费">
            <InputNumber
              style={{ width: '100%' }}
              prefix="¥"
              precision={2}
              min={0}
              placeholder="请输入金额（元）"
              disabled={modalStoreId !== undefined}
            />
          </Form.Item>
          <Form.Item name="rentFee" label="房租费">
            <InputNumber
              style={{ width: '100%' }}
              prefix="¥"
              precision={2}
              min={0}
              placeholder="请输入金额（元）"
              disabled={modalStoreId !== undefined}
            />
          </Form.Item>
          <Form.Item name="propertyFee" label="物业费">
            <InputNumber
              style={{ width: '100%' }}
              prefix="¥"
              precision={2}
              min={0}
              placeholder="请输入金额（元）"
              disabled={modalStoreId !== undefined}
            />
          </Form.Item>
          <Form.Item name="laborCost" label="人力成本">
            <InputNumber
              style={{ width: '100%' }}
              prefix="¥"
              precision={2}
              min={0}
              placeholder="请输入金额（元）"
              disabled={modalStoreId !== undefined}
            />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <TextArea rows={4} placeholder="请输入备注（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CashflowData;
